import { useState, FormEvent } from 'react';
import emailjs from '@emailjs/browser';

const projectTypes = [
  'Décoration Événementielle – Anniversaire enfant',
  'Décoration Événementielle – Mariage',
  'Décoration Événementielle – Anniversaire adulte',
  'Décoration Événementielle – Événement entreprise',
  "Décoration d'Intérieur",
  'Création 3D – Figurines / Objets',
  'Création 3D – Goodies',
  'Conseil & Orientation',
  'Autre',
];

const budgetRanges = [
  'Moins de 200 000 XOF',
  '200 000 – 500 000 XOF',
  '500 000 – 1 000 000 XOF',
  '1 000 000 – 3 000 000 XOF',
  'Plus de 3 000 000 XOF',
  'À définir ensemble',
];

type Status = 'idle' | 'sending' | 'success' | 'error';

const ContactForm = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');

    // Read all form field values
    const getVal = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)?.value ?? '';

    const templateParams = {
      from_name: `${getVal('prenom')} ${getVal('nom')}`,
      nom: getVal('nom'),
      prenom: getVal('prenom'),
      email: getVal('email'),
      reply_to: getVal('email'),
      telephone: getVal('telephone'),
      type_projet: getVal('type_projet'),
      budget: getVal('budget'),
      date_souhaitee: getVal('date_souhaitee'),
      message: getVal('message'),
    };

    try {
      await emailjs.send(
        'service_hdov8s6',
        'template_6jw7ana',
        templateParams,
        'y459mQFz3zIKOmCR8'
      );
      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch (err: any) {
      const msg = err?.message || err?.text || JSON.stringify(err);
      console.error('EmailJS error:', err);
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 lg:p-10">
      {status === 'success' ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-5">
            <i className="ri-checkbox-circle-line text-green-500 text-3xl" />
          </div>
          <h3 className="font-serif text-2xl text-brand-anthracite mb-3">Message envoyé !</h3>
          <p className="text-brand-gray mb-6">
            Merci pour votre demande. Ashley vous recontactera dans les 24–48h ouvrées.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="px-6 py-2.5 rounded-full text-sm font-medium text-white cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
          >
            Envoyer une autre demande
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
          {/* Identité */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="nom" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                Nom <span className="text-brand-fuchsia">*</span>
              </label>
              <input
                id="nom"
                name="nom"
                type="text"
                required
                placeholder="Votre nom"
                className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
              />
            </div>
            <div>
              <label htmlFor="prenom" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                Prénom <span className="text-brand-fuchsia">*</span>
              </label>
              <input
                id="prenom"
                name="prenom"
                type="text"
                required
                placeholder="Votre prénom"
                className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                Email <span className="text-brand-fuchsia">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="votre@email.com"
                className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
              />
            </div>
            <div>
              <label htmlFor="telephone" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                Téléphone
              </label>
              <input
                id="telephone"
                name="telephone"
                type="tel"
                placeholder="+225 07 18 62 47 06"
                className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
              />
            </div>
          </div>

          {/* Type de projet */}
          <div>
            <label htmlFor="type_projet" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
              Type de projet <span className="text-brand-fuchsia">*</span>
            </label>
            <div className="relative">
              <select
                id="type_projet"
                name="type_projet"
                required
                className="w-full appearance-none px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition cursor-pointer"
              >
                <option value="">Sélectionnez un type de projet</option>
                {projectTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-brand-gray" />
              </div>
            </div>
          </div>

          {/* Budget + Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="budget" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                Budget estimé
              </label>
              <div className="relative">
                <select
                  id="budget"
                  name="budget"
                  className="w-full appearance-none px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition cursor-pointer"
                >
                  <option value="">Sélectionnez une fourchette</option>
                  {budgetRanges.map((range) => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-brand-gray" />
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="date_souhaitee" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
                Date souhaitée
              </label>
              <input
                id="date_souhaitee"
                name="date_souhaitee"
                type="date"
                className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-xs font-medium text-brand-anthracite mb-1.5 uppercase tracking-wide">
              Décrivez votre projet <span className="text-brand-fuchsia">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={5}
              maxLength={500}
              placeholder="Décrivez votre projet, vos envies, le lieu, le thème souhaité..."
              onChange={(e) => setCharCount(e.target.value.length)}
              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-brand-beige/40 text-brand-anthracite placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-fuchsia/30 focus:border-brand-fuchsia transition resize-none"
            />
            <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-brand-fuchsia' : 'text-brand-gray'}`}>
              {charCount}/500 caractères
            </p>
          </div>

          {/* Note consultation */}
          <div className="flex gap-3 items-start bg-brand-beige rounded-xl p-4">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-brand-coral text-base" />
            </div>
            <p className="text-xs text-brand-gray leading-relaxed">
              <strong className="text-brand-anthracite">Consultation préalable :</strong> Pour tout projet, une consultation de <strong className="text-brand-fuchsia">20 000 XOF</strong> est requise. Elle inclut l&apos;analyse de votre projet et un plan d&apos;action — déductible si votre projet est confirmé.
            </p>
          </div>

          {status === 'error' && (
            <p className="text-sm text-red-500 text-center">
              {errorMsg || 'Une erreur est survenue. Veuillez réessayer.'}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full py-4 rounded-full text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
          >
            {status === 'sending' ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" /> Envoi en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-send-plane-2-line" /> Envoyer ma demande de devis
              </span>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
